/**
 * The cards — `bimgo-ai-web`'s `renderToolCard` (saved, not shared) and the
 * folder card from `ToolsList.tsx`.
 *
 * A skill card: category glyph, name with a "Published" badge, one line of
 * description, "Revit 2024 · Aug 20", then the move-to-folder menu and the
 * enabled switch on the right — the two controls that stop the click from
 * opening the skill.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { describeTarget, type Skill, type SkillFolder } from "../../lib/skills";
import { getSkillCategory, type SkillCategoryId } from "../../lib/skillCategory";
import { BoxIcon, BuildingIcon, ClockIcon, CloudUploadIcon, FolderIcon, FolderInputIcon, LoaderIcon, MoreIcon, PlugZapIcon, PointerIcon, WrenchIcon } from "./icons";

export function CategoryGlyph({ category }: { category: SkillCategoryId }) {
  const icon =
    category === "mep" ? <PlugZapIcon /> : category === "structure" ? <BuildingIcon /> : category === "revit-ops" ? <PointerIcon /> : <WrenchIcon />;
  return <span className={`skill-glyph is-${category}`} aria-hidden="true">{icon}</span>;
}

export function formatCardDate(iso: string, locale: string): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleDateString(locale === "zh" ? "zh-TW" : "en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function SkillCard({
  skill,
  folders,
  mayToggle,
  busy,
  onOpen,
  onMove,
  onToggle,
}: {
  skill: Skill;
  folders: readonly SkillFolder[];
  mayToggle: boolean;
  /** Moving or toggling this one right now. */
  busy: boolean;
  onOpen: () => void;
  onMove: (folderName: string | null) => void;
  onToggle: (enabled: boolean) => void;
}) {
  const { t, locale } = useLocale();
  const category = getSkillCategory(skill);
  /** "Move to Revit 2021 folder" — the year the folders on offer belong to. */
  const moveLabel = t("skills.moveToTargetFolder", { target: describeTarget(skill.target, t("skills.unknownVersion")) });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <div
      className={`skill-card${skill.enabled ? "" : " is-disabled"}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      data-skill={skill.id}
    >
      <CategoryGlyph category={category.id} />
      <div className="skill-card-body">
        <div className="skill-card-title">
          <span className="skill-card-name">{skill.displayName}</span>
          {skill.isPublished ? (
            <span className="skill-badge is-published"><CloudUploadIcon size={10} /> {t("skills.published")}</span>
          ) : null}
        </div>
        <p className="skill-card-desc">{skill.description}</p>
        <div className="skill-card-meta">
          <BoxIcon size={12} />
          <span>{describeTarget(skill.target, t("skills.unknownVersion"))}</span>
          <span className="sep">·</span>
          <ClockIcon size={12} />
          <span>{formatCardDate(skill.createdAt, locale)}</span>
        </div>
      </div>
      <div className="skill-card-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <div className="skill-menu" ref={menuRef}>
          <button type="button" className="icon-btn" disabled={busy} aria-label={moveLabel} aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
            {busy ? <LoaderIcon size={14} /> : <MoreIcon />}
          </button>
          {menuOpen ? (
            <div className="skill-menu-pop" role="menu">
              {/**
               * "Move to Revit 2021 folder", not "Move to folder" (owner).
               *
               * A folder is a directory inside **one** year's store, so the only folders this
               * menu can offer are that year's — the caller filters them, and 2021's "Export"
               * is a different directory from 2024's. The label is where that rule is
               * visible: a list of three names under a heading that says "folder" invites the
               * question "where are my other folders?", and the year answers it before it is
               * asked.
               */}
              <div className="skill-menu-label">{moveLabel}</div>
              <button type="button" role="menuitem" className="skill-menu-item" disabled={!skill.parentFolder} onClick={() => { setMenuOpen(false); onMove(null); }}>
                <FolderInputIcon size={14} /> {t("skills.rootFolder")}
              </button>
              {folders.map((folder) => (
                <button key={folder.name} type="button" role="menuitem" className="skill-menu-item" disabled={skill.parentFolder === folder.name} onClick={() => { setMenuOpen(false); onMove(folder.name); }}>
                  <FolderIcon size={14} /> <span className="truncate">{folder.name}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {mayToggle ? (
          <label className="skill-switch" title={t("toolEditor.enabled")}>
            <input type="checkbox" checked={skill.enabled} disabled={busy} onChange={(e) => onToggle(e.target.checked)} aria-label={t("toolEditor.enabled")} />
            <span className="skill-switch-track" aria-hidden="true" />
          </label>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One card per directory, and **it names its year** (owner).
 *
 * Two folders can share a name — "Export" is its own directory in 2024 and in 2025 — so
 * without the year the two cards are indistinguishable, and opening one is a guess. It sits
 * beside the count as the folder's second line, the way the skill card carries its target.
 */
export function FolderCard({ folder, onOpen }: { folder: SkillFolder; onOpen: () => void }) {
  const { t } = useLocale();
  return (
    <button
      type="button"
      className="folder-card"
      onClick={onOpen}
      data-folder={folder.name}
      data-folder-version={folder.version}
    >
      <span className="folder-glyph" aria-hidden="true"><FolderIcon filled /></span>
      <span className="folder-card-body">
        <span className="folder-card-name">{folder.name}</span>
        <span className="folder-card-meta">
          <span className="folder-card-count">{t("skills.skillCount").replace("{count}", String(folder.skillCount))}</span>
          {folder.version ? (
            <>
              <span className="sep">·</span>
              {/* The year alone: the vendor is in the page heading. */}
              <span className="folder-card-versions">{folder.version}</span>
            </>
          ) : null}
        </span>
      </span>
    </button>
  );
}
